<script>
import axios from 'axios';
import Multiselect from '@vueform/multiselect';
import { api } from '@/helpers/common/api';

const CORES_PADRAO = [
  '#dc3545', '#fd7e14', '#ffc107', '#198754', '#20c997',
  '#0dcaf0', '#0d6efd', '#6610f2', '#d63384', '#6c757d',
];

export default {
  name: 'PainelEtiquetas',
  components: { Multiselect },
  props: {
    contato: { type: Object, required: true },
  },
  data() {
    return {
      todasEtiquetas: [],
      etiquetasDoContato: Array.isArray(this.contato.rotulos) ? [...this.contato.rotulos] : [],
      idsAtuais: (Array.isArray(this.contato.rotulos) ? this.contato.rotulos : []).map((r) => r.id),
      carregando: false,
      erro: null,
      // Termo de busca digitado no Multiselect (pra mostrar "Criar X")
      termoBusca: '',
      coresDisponiveis: CORES_PADRAO,
      salvandoNova: false,
      // Cor selecionada pra criação inline (slot #noresults do Multiselect)
      corCriacaoRapida: CORES_PADRAO[0],
    };
  },
  computed: {
    opcoesMultiselect() {
      return this.todasEtiquetas.map((e) => ({
        value: e.id,
        label: e.nome,
        cor: e.cor_referencia || '#6c757d',
      }));
    },
  },
  watch: {
    'contato.rotulos': {
      handler(novo) {
        const lista = Array.isArray(novo) ? novo : [];
        this.etiquetasDoContato = [...lista];
        this.idsAtuais = lista.map((r) => r.id);
      },
      deep: true,
    },
    'contato.id': {
      handler() { this.carregarTodas(); },
    },
  },
  async mounted() {
    await this.carregarTodas();
  },
  methods: {
    async carregarTodas() {
      this.carregando = true;
      this.erro = null;
      try {
        // Usa api.read (mesma helper do ContatoGrid) que já desempacota
        // o envelope { data: ... } do BaseController/RotuloController.
        const resultado = await api.read('/rotulo?limit=9999');
        const lista = Array.isArray(resultado) ? resultado : (resultado && resultado.data) || [];
        // eslint-disable-next-line no-console
        console.log(`[PainelEtiquetas] ${lista.length} etiquetas carregadas`);
        this.todasEtiquetas = lista;
      } catch (error) {
        this.erro = `Falha ao carregar etiquetas: ${error.message}`;
        this.todasEtiquetas = [];
      } finally {
        this.carregando = false;
      }
    },
    async onSelect(rotuloId) {
      this.erro = null;
      try {
        await axios.post('/contato/etiqueta', { contato_id: this.contato.id, rotulo_id: rotuloId });
        const eti = this.todasEtiquetas.find((e) => e.id === rotuloId);
        if (eti && !this.etiquetasDoContato.find((e) => e.id === rotuloId)) {
          this.etiquetasDoContato.push(eti);
        }
      } catch (error) {
        // reverte se backend falhou
        this.idsAtuais = this.idsAtuais.filter((id) => id !== rotuloId);
        this.erro = (error.response && error.response.data && error.response.data.error) || error.message;
      }
    },
    async onDeselect(rotuloId) {
      this.erro = null;
      try {
        await axios.delete(`/contato/etiqueta/${rotuloId}`, { data: { contato_id: this.contato.id } });
        this.etiquetasDoContato = this.etiquetasDoContato.filter((e) => e.id !== rotuloId);
      } catch (error) {
        this.idsAtuais = [...this.idsAtuais, rotuloId];
        this.erro = (error.response && error.response.data && error.response.data.error) || error.message;
      }
    },
    async criarRapida() {
      // Atalho: criar etiqueta com o termo de busca atual + cor selecionada
      const nome = (this.termoBusca || '').trim();
      if (!nome) return;
      await this._criarComNome(nome, this.corCriacaoRapida);
      this.termoBusca = '';
    },
    /**
     * Helper que cria nova etiqueta E atribui ao contato — usado por
     * `criarNova` (form completo) e `criarRapida` (inline no dropdown).
     */
    async _criarComNome(nomeRaw, cor) {
      const nome = (nomeRaw || '').trim();
      if (!nome) { this.erro = 'Informe o nome da etiqueta.'; return; }
      this.salvandoNova = true;
      this.erro = null;
      try {
        const { data: nova } = await axios.post('/rotulo', {
          nome,
          cor_referencia: cor || CORES_PADRAO[0],
          ativo: true,
        });
        if (nova && nova.id) {
          this.todasEtiquetas.push(nova);
          await axios.post('/contato/etiqueta', { contato_id: this.contato.id, rotulo_id: nova.id });
          this.etiquetasDoContato.push(nova);
          this.idsAtuais = [...this.idsAtuais, nova.id];
        }
      } catch (error) {
        this.erro = (error.response && error.response.data && error.response.data.error) || error.message;
      } finally {
        this.salvandoNova = false;
      }
    },
  },
};
</script>

<template>
  <div class="painel-etiquetas">
    <h6 class="mb-3"><i class="ri-price-tag-3-line me-1"></i>Etiquetas do contato</h6>

    <div v-if="erro" class="alert alert-danger py-2 small">{{ erro }}</div>

    <label class="form-label small">Etiquetas atribuídas</label>

    <Multiselect
      v-if="!carregando"
      v-model="idsAtuais"
      v-model:search="termoBusca"
      :options="opcoesMultiselect"
      mode="tags"
      label="label"
      value-prop="value"
      :searchable="true"
      :close-on-select="false"
      placeholder="Digite ou selecione etiquetas..."
      no-options-text="Nenhuma etiqueta cadastrada. Digite o nome pra criar."
      @select="onSelect"
      @deselect="onDeselect"
    >
      <template #tag="{ option, handleTagRemove }">
        <span class="multiselect-tag" :style="{ background: option.cor }">
          {{ option.label }}
          <span class="multiselect-tag-remove" @click.prevent="handleTagRemove(option, $event)">
            <span class="multiselect-tag-remove-icon"></span>
          </span>
        </span>
      </template>
      <template #option="{ option }">
        <span class="badge me-2" :style="{ backgroundColor: option.cor }">&nbsp;</span>
        {{ option.label }}
      </template>
      <!-- Slot quando busca não retorna nada — mostra botão de criar inline -->
      <template #noresults>
        <div class="criar-rapida-card">
          <div class="small text-muted mb-2">
            Nenhuma etiqueta encontrada com <strong>"{{ termoBusca }}"</strong>
          </div>
          <div class="d-flex align-items-center gap-1 mb-2">
            <span class="small me-1">Cor:</span>
            <button
              v-for="c in coresDisponiveis"
              :key="c"
              type="button"
              class="cor-swatch-mini"
              :class="{ ativa: corCriacaoRapida === c }"
              :style="{ backgroundColor: c }"
              :title="c"
              @click.stop="corCriacaoRapida = c"
            ></button>
          </div>
          <button
            type="button"
            class="btn btn-primary btn-sm w-100"
            :disabled="salvandoNova"
            @click.stop="criarRapida"
          >
            <i v-if="salvandoNova" class="ri-loader-4-line spin me-1"></i>
            <i v-else class="ri-add-line me-1"></i>
            {{ salvandoNova ? 'Criando...' : `Criar "${termoBusca}" e atribuir` }}
          </button>
        </div>
      </template>
    </Multiselect>
    <div v-else class="form-control text-muted small d-flex align-items-center" style="height: 38px;">
      <i class="ri-loader-4-line spin me-2"></i>Carregando etiquetas...
    </div>
    <small v-if="!carregando" class="text-muted d-block mt-1">
      <strong>{{ todasEtiquetas.length }}</strong> etiqueta(s) disponível(is) no sistema
    </small>

    <!-- Resumo embaixo -->
    <div v-if="etiquetasDoContato.length" class="mt-3 small text-muted">
      <strong>{{ etiquetasDoContato.length }}</strong> etiqueta(s) atribuída(s)
    </div>
  </div>
</template>

<style scoped>
.painel-etiquetas :deep(.multiselect) {
  --ms-tag-bg: var(--vz-primary);
  --ms-tag-color: #fff;
  --ms-radius: var(--vz-border-radius);
  --ms-border-color: var(--vz-input-border);
  --ms-bg: var(--vz-input-bg);
}
.painel-etiquetas :deep(.multiselect-tags) {
  padding: 4px;
  gap: 4px;
}
.painel-etiquetas :deep(.multiselect-tag) {
  color: #fff;
  font-weight: 500;
  font-size: 0.7rem;
  padding: 0.18rem 0.5rem;
  border-radius: 0.85rem;
  margin: 1px 2px;
}
.painel-etiquetas :deep(.multiselect-tag-remove) {
  margin-left: 4px;
}

.card-criar-etiqueta {
  background: var(--vz-tertiary-bg);
  border: 1px solid var(--vz-border-color);
  border-radius: 0.375rem;
}
.cor-swatch {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  transition: transform 0.1s, border-color 0.1s;
}
.cor-swatch:hover { transform: scale(1.15); }
.cor-swatch.ativa { border-color: var(--vz-body-color); transform: scale(1.15); }

/* Criação rápida inline no slot #noresults do Multiselect */
.criar-rapida-card {
  padding: 0.75rem;
  background: var(--vz-tertiary-bg);
}
.cor-swatch-mini {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  transition: transform 0.1s, border-color 0.1s;
}
.cor-swatch-mini:hover { transform: scale(1.2); }
.cor-swatch-mini.ativa { border-color: var(--vz-body-color); transform: scale(1.2); }

.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
